import React from "react";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckIcon from "@mui/icons-material/Check";
import MathExpressionNew from "../common/mathjax";
import { resolveSectionLetter } from "../../utils/sectionUtils";
import { ExerciseReviewContentProps } from "./types";
import "./ExerciseReviewContent.scss";

export const ExerciseReviewContent: React.FC<ExerciseReviewContentProps> = ({
  data,
  notification,
  showTeacherComment = true,
  onOpenExercise,
}) => {
  const { t } = useTranslation();

  const metadata = notification?.metadata || {};

  const exerciseId = data?.exerciseId !== undefined
    ? data.exerciseId
    : metadata.exercise_id
    ? Number(metadata.exercise_id)
    : null;

  const exerciseTitle =
    data?.exerciseTitle ||
    metadata.exercise_title ||
    metadata.exercise_name ||
    "";

  const questionText = data?.questionText || metadata.question_text || "";

  const sectionLetter = resolveSectionLetter(
    data
      ? {
          question_letter: data.questionLetter,
          question_index: data.questionIndex,
          question_letter_key: data.questionLetterKey,
        }
      : metadata
  );

  const sectionTitle = sectionLetter
    ? t("sectionReviewTitle", { letter: sectionLetter })
    : "";

  const topic = data?.topicName || metadata.topic || metadata.topic_name || "";
  const difficulty =
    data?.difficultyName || metadata.difficulty || metadata.difficulty_name || "";
  const rawUnits =
    data?.levelUnits !== undefined
      ? data.levelUnits
      : metadata.level_units ?? metadata.units ?? null;
  const levelName = data?.levelName || metadata.level_name || "";
  const unitsText =
    rawUnits !== null && rawUnits !== undefined
      ? `${rawUnits} ${t("unitsShort")}`
      : levelName;

  const rawDate =
    data?.solvedDate || metadata.solved_date || metadata.submitted_at;
  const formatSolvedDate = (dateStr?: string | null): string => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const day = d.getDate();
      const month = d.getMonth() + 1;
      return t("solvedAtDate", { date: `${day}.${month}` });
    }
    return t("solvedAtDate", { date: dateStr });
  };

  const solvedDateText = rawDate ? formatSolvedDate(rawDate) : "";

  const subtitleParts: string[] = [];
  if (topic) subtitleParts.push(topic);
  if (difficulty) subtitleParts.push(difficulty);
  if (unitsText) subtitleParts.push(unitsText);
  if (solvedDateText) subtitleParts.push(solvedDateText);

  const subtitle = subtitleParts.join(" · ");

  const status = data?.status || metadata.status || "partial";
  const summaryContent =
    data?.analysisSummary || metadata.analysis_summary || "";
  const strengthsList: string[] =
    data?.strengths ||
    (Array.isArray(metadata.strengths) ? metadata.strengths : []);
  const weaknessesList: string[] =
    data?.weaknesses ||
    (Array.isArray(metadata.weaknesses) ? metadata.weaknesses : []);
  const issuesList: string[] =
    data?.issues ||
    (Array.isArray(metadata.issues) ? metadata.issues : []);

  const teacherCommentText =
    data?.teacherComment || notification?.body || "";

  const getSummaryStatusClass = (st: string) => {
    switch (st) {
      case "correct":
        return "summary-status-correct";
      case "partial":
        return "summary-status-partial";
      case "missing":
        return "summary-status-missing";
      case "incorrect":
      default:
        return "summary-status-incorrect";
    }
  };

  const handleOpenExercise = () => {
    if (exerciseId && onOpenExercise) {
      onOpenExercise(exerciseId);
    }
  };

  return (
    <div className="exercise-review-content" dir="rtl">
      {/* 1. Exercise Preview Header Card */}
      <div className="exercise-preview-card">
        <div className="exercise-preview-header">
          <div className="exercise-title-group">
            <span className="exercise-card-title">{exerciseTitle}</span>
            {subtitle && (
              <span className="exercise-card-subtitle">{subtitle}</span>
            )}
          </div>

          {exerciseId && onOpenExercise && (
            <button
              type="button"
              className="view-exercise-btn"
              onClick={handleOpenExercise}
              aria-label={t("viewExercise")}
            >
              <span className="btn-text">{t("viewExercise")}</span>
              <ChevronLeftIcon className="btn-chevron-icon" fontSize="small" />
            </button>
          )}
        </div>

        {/* Highlighted Question / Section Block */}
        {(sectionTitle || questionText) && (
          <div className="exercise-question-block">
            {sectionTitle && (
              <span className="question-section-label">{sectionTitle}</span>
            )}
            {questionText && (
              <div className="question-prompt-box">
                <MathExpressionNew content={questionText} variant="chat" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. Teacher's Comment Bubble (Conditionally Hidden in Real-Time Flow) */}
      {showTeacherComment && teacherCommentText && (
        <div className="teacher-comment-card">
          <span className="comment-label">{t("teacherComment")}</span>
          <p className="comment-body">{teacherCommentText}</p>
        </div>
      )}

      {/* 3. Analysis Summary Box */}
      {summaryContent && (
        <div className={`analysis-summary-box ${getSummaryStatusClass(status)}`}>
          <div className="analysis-summary-header">
            <span className="summary-title-label">{t("analysisSummaryTitle")}</span>
          </div>
          <div className="analysis-summary-content">
            <MathExpressionNew content={summaryContent} variant="chat" />
          </div>
        </div>
      )}

      {/* 4. Strengths & Points to Maintain */}
      {strengthsList.length > 0 && (
        <div className="pillar-card strengths-card">
          <h4 className="pillar-title">{t("strengthsTitle")}</h4>
          <ul className="pillar-list strengths-list">
            {strengthsList.map((item, idx) => (
              <li key={idx} className="pillar-list-item strength-item">
                <span className="check-icon-wrapper">
                  <CheckIcon className="green-check-icon" fontSize="small" />
                </span>
                <span className="item-text">
                  <MathExpressionNew content={item} variant="chat" />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 5. Errors & Points for Improvement */}
      <div className="pillar-card weaknesses-card">
        <h4 className="pillar-title">{t("weaknessesTitle")}</h4>
        {weaknessesList.length > 0 ? (
          <ul className="pillar-list weaknesses-list">
            {weaknessesList.map((item, idx) => (
              <li key={idx} className="pillar-list-item weakness-item">
                <span className="bullet-point" />
                <span className="item-text">
                  <MathExpressionNew content={item} variant="chat" />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="no-weaknesses-text">{t("noWeaknessesFound")}</p>
        )}
      </div>

      {/* 6. General Notes */}
      {issuesList.length > 0 && (
        <div className="pillar-card general-notes-card">
          <h4 className="pillar-title">{t("generalNotesTitle")}</h4>
          <ul className="pillar-list general-notes-list">
            {issuesList.map((item, idx) => (
              <li key={idx} className="pillar-list-item general-note-item">
                <span className="bullet-point bullet-point--neutral" />
                <span className="item-text">
                  <MathExpressionNew content={item} variant="chat" />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ExerciseReviewContent;
